import { useEffect, useState } from "react";
import { ROUTES, type PasskeySummary, type SessionUser } from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { useTheme, type Theme } from "../lib/theme.js";
import { createPasskey, passkeyErrorMessage, passkeysSupported } from "../lib/webauthn.js";
import type { LinkOutcome, Route } from "../lib/router.js";
import { Icon } from "../components/Icon.js";
import { InventoriesPanel } from "../components/InventoriesPanel.js";
import { ModelKeysPanel } from "../components/ModelKeysPanel.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { VcnetCredit } from "../components/VcnetCredit.js";
import { Segmented } from "@vcnet/ui";
import { useToast } from "../components/Toast.js";

const day = (seconds: number) =>
  new Date(seconds * 1000).toLocaleDateString(undefined, { dateStyle: "medium" });

/**
 * Who you are, which inventories you have (and making another), getting your
 * data out, how the app looks, your own AI model keys, and passkeys and connected Google or Microsoft
 * accounts: what utucat can prove you are besides a link in your inbox. Last,
 * ending every session at once. Reached from the account menu at the foot of
 * the sidebar (or More, on a phone).
 */
export function Settings({
  user,
  onNavigate,
  linkOutcome,
  onLinkOutcomeSeen,
  onSignOutEverywhere,
}: {
  user: SessionUser;
  onNavigate: (route: Route) => void;
  /** What connecting Google or Microsoft just did, said once. */
  linkOutcome: LinkOutcome | null;
  onLinkOutcomeSeen: () => void;
  onSignOutEverywhere: () => void;
}) {
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [theme, setTheme] = useTheme();
  const toast = useToast();

  useEffect(() => {
    if (!linkOutcome) return;
    if (linkOutcome.ok) toast(linkOutcome.message);
    else setLinkError(linkOutcome.message);
    onLinkOutcomeSeen();
  }, [linkOutcome, onLinkOutcomeSeen, toast]);

  function signOutEverywhere() {
    const ok = window.confirm(
      "Sign out of utucat on every device, this one included? Each one will need to sign in again.",
    );
    if (ok) onSignOutEverywhere();
  }

  useEffect(() => {
    let live = true;
    void api.passkeys
      .list()
      .then(({ passkeys }) => {
        if (live) setPasskeys(passkeys);
      })
      .catch(() => {
        if (live) setError("Could not load your passkeys.");
      });
    return () => {
      live = false;
    };
  }, []);

  async function addPasskey(event: React.FormEvent) {
    event.preventDefault();
    setBusy("add");
    setError(null);
    try {
      const { options } = await api.passkeys.registerOptions();
      const response = await createPasskey(options);
      const { passkeys } = await api.passkeys.registerVerify(response, name.trim());
      setPasskeys(passkeys);
      setName("");
      toast("Passkey added.");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : passkeyErrorMessage(problem));
    } finally {
      setBusy(null);
    }
  }

  async function removePasskey(id: string) {
    setBusy(id);
    setError(null);
    try {
      setPasskeys((await api.passkeys.remove(id)).passkeys);
      toast("Passkey removed.");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader title="Settings" />

      <Panel>
        <div className="identity">
          <span className="avatar avatar-lg">{(user.name || user.email).charAt(0).toUpperCase()}</span>
          <span className="identity-text">
            <strong>{user.name || user.email}</strong>
            {user.name && <span className="muted">{user.email}</span>}
          </span>
          {user.isAdmin && <span className="pill">Admin</span>}
        </div>
      </Panel>

      <InventoriesPanel user={user} />

      <Panel title="Export" note="Take a copy of this inventory's items out.">
        <button className="button" type="button" onClick={() => onNavigate({ name: "export" })}>
          <Icon name="download" size={18} />
          Export items
        </button>
      </Panel>

      <Panel title="Appearance">
        <Segmented<Theme>
          label="Theme"
          value={theme}
          onChange={setTheme}
          options={[
            { value: "auto", label: "Match device" },
            { value: "light", label: "Light" },
            { value: "dark", label: "Dark" },
          ]}
        />
      </Panel>

      <ModelKeysPanel />

      <Panel title="Passkeys" note="Sign in with your fingerprint, face or PIN. Email links keep working.">
        {error && <Notice tone="error">{error}</Notice>}

        {passkeys === null ? (
          <div className="skeleton-line" />
        ) : passkeys.length === 0 ? (
          <p className="muted small">No passkeys yet.</p>
        ) : (
          <ul className="rows">
            {passkeys.map((passkey) => (
              <li key={passkey.id} className="row-item">
                <span className="row-icon">
                  <Icon name="key" size={18} />
                </span>
                <span className="row-text">
                  <strong>{passkey.name || "Unnamed passkey"}</strong>
                  <span className="muted small">
                    Added {day(passkey.createdAt)} ·{" "}
                    {passkey.lastUsedAt ? `last used ${day(passkey.lastUsedAt)}` : "never used"}
                  </span>
                </span>
                <button
                  className="button button-ghost button-small"
                  type="button"
                  disabled={busy === passkey.id}
                  onClick={() => void removePasskey(passkey.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {!passkeysSupported() ? (
          <p className="muted small">This browser does not support passkeys.</p>
        ) : (
          <form className="inline-add" onSubmit={addPasskey}>
            <input
              className="input"
              aria-label="Name for the new passkey (optional)"
              placeholder="Name it, like “My laptop”"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <button className="button" type="submit" disabled={busy === "add"}>
              <Icon name="plus" size={18} />
              {busy === "add" ? "Adding…" : "Add passkey"}
            </button>
          </form>
        )}
      </Panel>

      <Panel
        title="Google and Microsoft"
        note="Connect one so it can sign you in. A work or school Microsoft account needs this before it can sign you in."
      >
        {linkError && <Notice tone="error">{linkError}</Notice>}
        <div className="row">
          {/* Navigations, not fetches: the provider's own screen comes next. */}
          <a className="button button-quiet" href={`${ROUTES.googleStart}?link=1`}>
            <img className="provider-logo" src="/brand/google.svg" alt="" width={18} height={18} />
            Connect Google
          </a>
          <a className="button button-quiet" href={`${ROUTES.microsoftStart}?link=1`}>
            <img className="provider-logo" src="/brand/microsoft.svg" alt="" width={18} height={18} />
            Connect Microsoft
          </a>
        </div>
      </Panel>

      <Panel
        title="Sessions"
        note="Lost a device, or signed in somewhere you should not have? This ends them all."
      >
        <button className="button button-quiet" type="button" onClick={signOutEverywhere}>
          <Icon name="log-out" size={18} />
          Sign out everywhere
        </button>
      </Panel>

      <footer className="app-credit">
        <VcnetCredit />
      </footer>
    </>
  );
}
